import type { FC, MouseEventHandler } from "react";

import { useDropzone } from "react-dropzone";

import { Button, classNames, Icon, Spinner, Text } from "@alpacahq/ui/old";

import type { DropzoneProps } from "./types";

export const FileDropzone: FC<DropzoneProps> = ({
  id,
  file,
  onUpload,
  onRemove,
  onError,
  label,
  placeholder,
  name,
  isRequired,
  isLoading,
  accept,
  maxSize,
  displayType = "File",
  maxSizeLabel,
}) => {
  const dropzone = useDropzone({
    maxFiles: 1,
    maxSize,
    accept,
    // Grab first file. Only one file is allowed but its always an array
    onDrop: (acceptedFiles: File[]) => {
      if (!acceptedFiles[0]) {
        return;
      }
      onUpload(acceptedFiles[0]);
    },
    onDropRejected: (fileRejections) => {
      if (!fileRejections[0]) {
        return;
      }
      onError(fileRejections[0]);
    },
  });

  const handleRemove: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.();
  };

  return (
    <div className="w-full">
      <div
        className={classNames(
          "flex h-16 w-full shrink-0 items-center justify-center gap-3 rounded border border-gray-200 border-dashed bg-white px-2 py-3",
          !file?.name && "cursor-pointer",
        )}
        {...dropzone.getRootProps()}
      >
        {isLoading ? (
          <Spinner className="h-9 w-9" />
        ) : file?.name ? (
          <>
            {onRemove ? (
              <Button
                aria-label={`Remove ${label}`}
                onClick={handleRemove}
                className="mr-3"
              >
                <Icon name="Trash" size="sm" />
              </Button>
            ) : (
              <Icon name="Document" size="sm" />
            )}
            <Text>{file.name}</Text>
          </>
        ) : (
          <>
            <input
              id={id ?? name}
              placeholder={placeholder?.toString()}
              required={isRequired}
              {...dropzone.getInputProps()}
              name={name}
            />
            <Icon
              name="ArrowDownTray"
              size="sm"
              aria-label={label}
              className="m-2"
            />
            <div>
              <Text>Select {displayType} to upload</Text>
              <Text className="mt-1 text-sm">
                Or Drag and drop your {displayType} here
              </Text>
            </div>
          </>
        )}
      </div>
      {maxSizeLabel && <Text className="mt-1 text-sm">{maxSizeLabel}</Text>}
    </div>
  );
};
